/**
 * Funciones de serialización y deserialización para el nodo de campo calculado.
 *
 * IMPORTANTE: Este archivo maneja dos tipos de variables:
 *
 * 1. **Variables de FORMULARIO**:
 *    - Cada campo del formulario es una variable individual
 *    - Tienen un `field.id` único y específico
 *    - En serialización: se incluye el `field` property
 *    - En deserialización: se usa `sourceConnection.field.id`
 *
 * 2. **Variables de CENTRO DE DATOS**:
 *    - Representan el resultado completo de una consulta de datos
 *    - Tienen `variable.id === variable.node_id` (condición switch)
 *    - En serialización: NO se incluye el `field` property, se usa nombre especial `var_switch_`
 *    - En deserialización: `field` es null, se usa `sourceConnection.source_node_id`
 *
 * La diferenciación se basa en la condición: `variable.id === variable.node_id`
 */

import type {
  CalculationElement,
  VariableType,
} from "../CalculatedFieldOperation/types";
import type { Operation } from "../CalculatedFieldOperation/utils/operations";
import type { NodeVariable } from "../../types";
import type {
  SerializedCalculatedNode,
  SerializedCalculation,
  SerializedInput,
  CalculationOperation,
  PrimitiveType,
  SerializedCalculatedInput,
} from "./types";

/**
 * Mapeo de operaciones del frontend a backend
 */
const OPERATION_MAPPING: Record<string, CalculationOperation> = {
  // String operations
  concatenate: "CONCATENATE",
  replace: "REPLACE",
  replace_all: "REPLACE_ALL",
  slice: "SLICE",
  trim: "TRIM",
  to_upper_case: "TO_UPPER_CASE",
  to_lower_case: "TO_LOWER_CASE",
  string_length: "STRING_LENGTH",
  repeat: "REPEAT",
  split: "SPLIT",

  // Number operations
  add: "ADD",
  subtract: "SUBTRACT",
  multiply: "MULTIPLY",
  divide: "DIVIDE",
  power: "POWER",
  sqrt: "SQRT",
  abs: "ABS",
  round: "ROUND",
  ceil: "CEIL",
  floor: "FLOOR",
  truncate: "TRUNCATE",
  log10: "LOG10",
  ln: "LN",
  exp: "EXP",
  number_to_string: "NUMBER_TO_STRING",

  // Date operations
  date_add: "DATE_ADD",
  date_subtract: "DATE_SUBTRACT",
  date_diff: "DATE_DIFF",
  date_get_format: "DATE_GET_FORMAT",
  date_get_year: "DATE_GET_YEAR",
  date_get_month: "DATE_GET_MONTH",
  date_get_day: "DATE_GET_DAY",
  date_get_hour: "DATE_GET_HOUR",
  date_get_minute: "DATE_GET_MINUTE",
  date_get_weekday: "DATE_GET_WEEKDAY",
  date_get_month_name: "DATE_GET_MONTH_NAME",
  date_get_timestamp: "DATE_GET_TIMESTAMP",
  date_to_string: "DATE_TO_STRING",
  date_to_words: "DATE_TO_WORDS",
  datetime_to_words: "DATETIME_TO_WORDS",

  // Boolean operations
  not: "NOT",
  and: "AND",
  or: "OR",
  xor: "XOR",
  nand: "NAND",
  nor: "NOR",
  xnor: "XNOR",
  boolean_to_number: "BOOLEAN_TO_NUMBER",
  boolean_to_string: "BOOLEAN_TO_STRING",

  // Array operations
  array_pop: "ARRAY_POP",
  array_shift: "ARRAY_SHIFT",
  array_splice: "ARRAY_SPLICE",
  array_sort_asc: "ARRAY_SORT_ASC",
  array_sort_desc: "ARRAY_SORT_DESC",
  array_reverse: "ARRAY_REVERSE",
  array_map: "ARRAY_MAP",
  array_filter: "ARRAY_FILTER",
  array_reduce: "ARRAY_REDUCE",
  array_length: "ARRAY_LENGTH",
  array_index_of: "ARRAY_INDEX_OF",
  array_filter_length: "ARRAY_FILTER_LENGTH",
  array_trim: "ARRAY_TRIM",
  array_chunk: "ARRAY_CHUNK",
  array_unique: "ARRAY_UNIQUE",
};

/**
 * Mapeo inverso de operaciones del backend a frontend
 */
const REVERSE_OPERATION_MAPPING: Record<CalculationOperation, string> = {
  // String operations
  CONCATENATE: "concatenate",
  REPLACE: "replace",
  REPLACE_ALL: "replace_all",
  SLICE: "slice",
  TRIM: "trim",
  TO_UPPER_CASE: "to_upper_case",
  TO_LOWER_CASE: "to_lower_case",
  STRING_LENGTH: "string_length",
  REPEAT: "repeat",
  SPLIT: "split",

  // Number operations
  ADD: "add",
  SUBTRACT: "subtract",
  MULTIPLY: "multiply",
  DIVIDE: "divide",
  POWER: "power",
  SQRT: "sqrt",
  ABS: "abs",
  ROUND: "round",
  CEIL: "ceil",
  FLOOR: "floor",
  TRUNCATE: "truncate",
  LOG10: "log10",
  LN: "ln",
  EXP: "exp",
  NUMBER_TO_STRING: "number_to_string",

  // Date operations
  DATE_ADD: "date_add",
  DATE_SUBTRACT: "date_subtract",
  DATE_DIFF: "date_diff",
  DATE_GET_FORMAT: "date_get_format",
  DATE_GET_YEAR: "date_get_year",
  DATE_GET_MONTH: "date_get_month",
  DATE_GET_DAY: "date_get_day",
  DATE_GET_HOUR: "date_get_hour",
  DATE_GET_MINUTE: "date_get_minute",
  DATE_GET_WEEKDAY: "date_get_weekday",
  DATE_GET_MONTH_NAME: "date_get_month_name",
  DATE_GET_TIMESTAMP: "date_get_timestamp",
  DATE_TO_STRING: "date_to_string",
  DATE_TO_WORDS: "date_to_words",
  DATETIME_TO_WORDS: "datetime_to_words",
  // Boolean operations
  NOT: "not",
  AND: "and",
  OR: "or",
  XOR: "xor",
  NAND: "nand",
  NOR: "nor",
  XNOR: "xnor",
  BOOLEAN_TO_NUMBER: "boolean_to_number",
  BOOLEAN_TO_STRING: "boolean_to_string",

  // Array operations
  ARRAY_POP: "array_pop",
  ARRAY_SHIFT: "array_shift",
  ARRAY_SPLICE: "array_splice",
  ARRAY_SORT_ASC: "array_sort_asc",
  ARRAY_SORT_DESC: "array_sort_desc",
  ARRAY_REVERSE: "array_reverse",
  ARRAY_MAP: "array_map",
  ARRAY_FILTER: "array_filter",
  ARRAY_REDUCE: "array_reduce",
  ARRAY_LENGTH: "array_length",
  ARRAY_INDEX_OF: "array_index_of",
  ARRAY_FILTER_LENGTH: "array_filter_length",
  ARRAY_TRIM: "array_trim",
  ARRAY_CHUNK: "array_chunk",
  ARRAY_UNIQUE: "array_unique",
};

/**
 * Serializa elementos de cálculo del frontend al formato del backend
 */
export function serializeCalculationElements(
  elements: CalculationElement[],
  operation: Operation,
  variableType: string,
  selectedVariable?: string,
  variables?: NodeVariable[],
  selectedDateGetFormat?: string,
  selectedDateToStringFormat?: string,
): SerializedCalculatedInput {
  const serializedInputs: SerializedInput[] = [];

  // Helper para obtener nodeId de una variable
  const getNodeIdFromVariable = (variableId: string): string | undefined => {
    if (!variables) return undefined;
    for (const nodeVar of variables) {
      const found = nodeVar.variables.find((v) => v.id === variableId);
      if (found) return nodeVar.nodeId;
    }
    return undefined;
  };

  const isInteroperabilityNodeVariable = (
    nodeId: string | undefined,
  ): boolean => {
    if (!variables || !nodeId) return false;
    const nodeVariable = variables.find((nv) => nv.nodeId === nodeId);
    return (
      nodeVariable?.type === "interoperability-node" ||
      nodeVariable?.type === "n8n-node"
    );
  };

  /**
   * Verifica si un nodo es de tipo expediente.
   */
  const isExpedienteNodeVariable = (nodeId: string | undefined): boolean => {
    if (!variables || !nodeId) return false;
    const nodeVariable = variables.find((nv) => nv.nodeId === nodeId);
    return nodeVariable?.type === "expedient-node";
  };

  // PRIMER INPUT: La variable seleccionada en el dropdown de operación (siempre order 0)
  if (selectedVariable) {
    // Buscar la variable para obtener sus datos completos
    const variable = variables
      ?.flatMap((nodeVar) => nodeVar.variables)
      .find((v) => v.id === selectedVariable);

    const serializedInput: SerializedInput = {
      name:
        variable && variable.id === variable.node_id
          ? `var_switch_${operation.name}_0`
          : `var_${operation.name}_0`,
      value: null,
      type: mapVariableTypeToBackend(variableType),
      order: 0,
    };

    if (variable?.global_type) {
      if (
        variable.global_type === "APPLICANT" ||
        variable.global_type === "CURRENT_TIME"
      ) {
        serializedInput.value = variable.id;
        serializedInput.global_type = variable.global_type;
      } else {
        serializedInput.value = variable.node_id || null;
        serializedInput.global_type = variable.global_type;
      }
    } else if (variable?.field_id && variable?.output_name) {
      // Es una variable select de formulario
      serializedInput.output_name = variable.output_name;
      serializedInput.field = variable.field_id;
      serializedInput.connection = variable.node_id;
    } else {
      const nodeId = getNodeIdFromVariable(selectedVariable);
      serializedInput.connection = nodeId;

      if (nodeId && variable && variable.id !== variable.node_id) {
        if (isInteroperabilityNodeVariable(nodeId)) {
          serializedInput.output_name = variable.output_name;
        } else if (isExpedienteNodeVariable(nodeId) && variable.output_name) {
          // Para nodo expediente: NO enviar field, solo connection y output_name
          // El backend usa output_name para identificar el campo del expediente
          serializedInput.output_name = variable.output_name;
        } else {
          serializedInput.field = selectedVariable;
        }
      }
    }

    serializedInputs.push(serializedInput);
  }

  // INPUTS ADICIONALES: Los elementos agregados manualmente (empiezan desde order 1)
  elements.forEach((element, index) => {
    let value: string | number | boolean | null = null;
    let connection: string | undefined = undefined;
    let field: string | undefined = undefined;
    let output: string | undefined = undefined;

    // Buscar la variable para obtener sus datos completos (si es una variable)
    const variable =
      element.type === "var"
        ? variables
            ?.flatMap((nodeVar) => nodeVar.variables)
            .find((v) => v.id === element.value)
        : undefined;

    // Determinar el tipo del input
    // Para operaciones date_add/date_subtract, los inputs adicionales (order > 0) siempre son ARRAY
    let inputType: PrimitiveType;
    if (
      (operation.name === "date_add" || operation.name === "date_subtract") &&
      variableType === "date"
    ) {
      // Para date_add/date_subtract, todos los inputs adicionales usan ARRAY
      inputType = "ARRAY";
    } else {
      inputType = mapVariableTypeToBackend(variableType);
    }

    if (element.type === "inp") {
      // Es un valor estático
      // Para operaciones de fecha que usan campos específicos (date_add, date_subtract)
      if (
        (operation.name === "date_add" || operation.name === "date_subtract") &&
        variableType === "date"
      ) {
        // Verificar si hay al menos un valor de tiempo ingresado
        const hasTimeValue =
          (element.hours && element.hours.trim() !== "") ||
          (element.days && element.days.trim() !== "") ||
          (element.months && element.months.trim() !== "") ||
          (element.years && element.years.trim() !== "");

        if (!hasTimeValue) {
          // Si no hay valores de tiempo, omitir este elemento
          return;
        }

        // Construir el valor como un objeto JSON con los campos de tiempo
        const timeValue = {
          hours: element.hours || "",
          days: element.days || "",
          months: element.months || "",
          years: element.years || "",
        };
        value = JSON.stringify(timeValue);
      } else {
        value = parseValueByType(element.value || "", variableType);
      }
      connection = undefined;
      field = undefined;
    } else if (element.type === "var") {
      if (variable?.global_type) {
        if (
          variable.global_type === "APPLICANT" ||
          variable.global_type === "CURRENT_TIME"
        ) {
          value = variable.id;
        } else {
          value = variable.node_id || null;
          connection = undefined;
          field = undefined;
        }
      } else if (
        variable?.field_id &&
        variable?.output_name &&
        variable.node_id
      ) {
        value = null;
        connection = variable.node_id;
        field = variable.field_id;
        output = variable.output_name;
      } else if (variable && variable?.output_name && variable.node_id) {
        // Variable con output_name (puede ser expediente u otro nodo)
        value = null;
        connection = variable.node_id;
        output = variable.output_name;
        // Para nodo expediente: NO enviar field, solo output_name
        // Para otros nodos con output_name: tampoco necesitan field
      } else {
        value = null;
        const nodeId = getNodeIdFromVariable(element.value || "");
        connection = nodeId;

        if (variable) {
          if (variable.id === variable.node_id) {
            field = undefined;
          } else if (
            nodeId &&
            isExpedienteNodeVariable(nodeId) &&
            variable.output_name
          ) {
            // Para nodo expediente: NO enviar field, solo output_name
            output = variable.output_name;
          } else {
            field = element.value;
          }
        } else {
          field = element.value;
        }
      }
    }

    const serializedInput: SerializedInput = {
      name:
        element.type === "var" && variable && variable.id === variable.node_id
          ? `var_switch_${operation.name}_${index + 1}`
          : `var_${operation.name}_${index + 1}`,
      value,
      type: inputType,
      connection,
      order: index + 1,
    };

    if (variable?.global_type) {
      serializedInput.global_type = variable.global_type;
    } else if (element.type === "var" && field && output) {
      serializedInput.field = field;
      serializedInput.output_name = output;
    } else if (element.type === "var" && output) {
      serializedInput.output_name = output;
    } else if (element.type === "var" && field) {
      if (
        variable &&
        connection &&
        isInteroperabilityNodeVariable(connection)
      ) {
        serializedInput.output_name = variable.id;
      } else {
        serializedInput.field = field;
      }
    }

    serializedInputs.push(serializedInput);
  });

  // Agregar input adicional para el formato de fecha en operaciones específicas
  if (operation.name === "date_get_format" && selectedDateGetFormat) {
    const formatInput: SerializedInput = {
      name: `format_${operation.name}_${serializedInputs.length}`,
      value: selectedDateGetFormat,
      type: "STRING",
      connection: undefined,
      order: serializedInputs.length,
    };
    serializedInputs.push(formatInput);
  }

  if (operation.name === "date_to_string" && selectedDateToStringFormat) {
    const formatInput: SerializedInput = {
      name: `format_${operation.name}_${serializedInputs.length}`,
      value: selectedDateToStringFormat,
      type: "STRING",
      connection: undefined,
      order: serializedInputs.length,
    };
    serializedInputs.push(formatInput);
  }

  const calculation: SerializedCalculation = {
    operation: OPERATION_MAPPING[operation.name] || "CONCATENATE",
    inputs: serializedInputs,
  };

  return {
    name: "calculation",
    value: null,
    type: "CALCULATED",
    calculations: [calculation],
  };
}

/**
 * Deserializa un input calculado del backend al frontend
 */
export function deserializeCalculatedInput(
  calculatedInput: SerializedCalculatedInput,
): {
  elements: CalculationElement[];
  operation: string;
  variableType: string;
  selectedVariable?: string;
  selectedDateGetFormat?: string;
  selectedDateToStringFormat?: string;
} {
  if (
    !calculatedInput.calculations ||
    calculatedInput.calculations.length === 0
  ) {
    return {
      elements: [],
      operation: "",
      variableType: "string",
    };
  }

  const calculation = calculatedInput.calculations[0];
  const operation =
    REVERSE_OPERATION_MAPPING[calculation.operation] || "concatenate";

  // Ordenar inputs por order (manejar undefined)
  const sortedInputs = calculation.inputs.sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );

  console.log(sortedInputs);

  // El primer input (order 0) determina el tipo de variable y la variable seleccionada
  const firstInput = sortedInputs[0];
  const variableType = mapBackendTypeToVariable(firstInput?.type || "STRING");
  const selectedVariable = firstInput?.connection;

  // Los inputs adicionales (order > 0) son los elementos o formatos
  const additionalInputs = sortedInputs.filter(
    (input) => (input.order || 0) > 0,
  );

  // Extraer formatos de fecha si existen
  let selectedDateGetFormat: string | undefined;
  let selectedDateToStringFormat: string | undefined;

  const formatInputs = additionalInputs.filter((input) =>
    input.name?.startsWith("format_"),
  );

  formatInputs.forEach((input) => {
    if (input.name?.includes("date_get_format")) {
      selectedDateGetFormat = input.value as string;
    } else if (input.name?.includes("date_to_string")) {
      selectedDateToStringFormat = input.value as string;
    }
  });

  // Los elementos reales (excluyendo los formatos)
  const realElements = additionalInputs.filter(
    (input) => !input.name?.startsWith("format_"),
  );

  const elements: CalculationElement[] = realElements.map((input, index) => {
    const baseElement = {
      id: `element_${index}`,
      type: input.connection ? "var" : "inp",
    } as CalculationElement;

    if (input.connection) {
      // Es una variable conectada
      baseElement.value = input.connection;
    } else {
      // Es un valor estático
      // Para operaciones de fecha que usan campos específicos (date_add, date_subtract)
      if (
        (operation === "date_add" || operation === "date_subtract") &&
        variableType === "date"
      ) {
        try {
          // Intentar parsear como JSON para obtener los campos de tiempo
          const timeValue = JSON.parse(input.value as string);
          baseElement.hours = timeValue.hours || "";
          baseElement.days = timeValue.days || "";
          baseElement.months = timeValue.months || "";
          baseElement.years = timeValue.years || "";
          baseElement.value = ""; // No usar el campo value para estas operaciones
        } catch {
          // Si no es JSON válido, tratar como valor normal
          baseElement.value = formatValueForFrontend(input.value, variableType);
        }
      } else {
        baseElement.value = formatValueForFrontend(input.value, variableType);
      }
    }
    console.log(baseElement);
    return baseElement;
  });

  return {
    elements,
    operation,
    variableType,
    selectedVariable,
    selectedDateGetFormat,
    selectedDateToStringFormat,
  };
}

/**
 * Serializa un nodo completo de campo calculado
 */
export function serializeCalculatedNode(
  displayName: string,
  refName: string,
  elements: CalculationElement[],
  operation: Operation,
  variableType: string,
  positionX: number = 0,
  positionY: number = 0,
  selectedVariable?: string,
  edge?: {
    source: string;
    source_handler: string;
    target_handler: string;
  },
): SerializedCalculatedNode {
  const calculatedInput = serializeCalculationElements(
    elements,
    operation,
    variableType,
    selectedVariable,
    undefined, // No tenemos variables aquí, será opcional
  );

  return {
    ref_name: refName,
    display_name: displayName,
    position_x: positionX,
    position_y: positionY,
    type: "CALCULATED",
    edge,
    inputs: [calculatedInput],
  };
}

/**
 * Deserializa un nodo de campo calculado del backend
 */
export function deserializeCalculatedNode(
  serializedNode: SerializedCalculatedNode,
): {
  displayName: string;
  refName: string;
  elements: CalculationElement[];
  operation: string;
  variableType: string;
  selectedVariable?: string;
  positionX: number;
  positionY: number;
  edge?: {
    source: string;
    source_handler: string;
    target_handler: string;
  };
} {
  const calculatedInput = serializedNode.inputs?.find(
    (input) => input.type === "CALCULATED",
  ) as SerializedCalculatedInput | undefined;

  const result = calculatedInput
    ? deserializeCalculatedInput(calculatedInput)
    : { elements: [], operation: "", variableType: "string" };

  return {
    displayName: serializedNode.display_name,
    refName: serializedNode.ref_name,
    elements: result.elements,
    operation: result.operation,
    variableType: result.variableType,
    selectedVariable: result.selectedVariable,
    positionX: serializedNode.position_x,
    positionY: serializedNode.position_y,
    edge: serializedNode.edge,
  };
}

/**
 * Mapea tipo de variable del frontend al backend
 */
function mapVariableTypeToBackend(variableType: string): PrimitiveType {
  switch (variableType) {
    case "string":
      return "STRING";
    case "number":
      return "FLOAT";
    case "boolean":
      return "BOOLEAN";
    case "date":
      return "DATE";
    case "array":
      return "ARRAY";
    default:
      return "STRING";
  }
}

/**
 * Mapea tipo del backend al frontend
 */
function mapBackendTypeToVariable(backendType: PrimitiveType): string {
  switch (backendType) {
    case "STRING":
    case "TEXT":
      return "string";
    case "INTEGER":
    case "FLOAT":
      return "number";
    case "BOOLEAN":
      return "boolean";
    case "DATE":
      return "date";
    case "ARRAY":
      return "array";
    default:
      return "string";
  }
}

/**
 * Parsea un valor según su tipo
 */
function parseValueByType(
  value: string,
  variableType: string,
): string | number | boolean | null {
  if (!value) return null;

  switch (variableType) {
    case "number": {
      const num = Number(value);
      return isNaN(num) ? null : num;
    }
    case "boolean":
      return value === "true";
    case "string":
    case "date":
    case "array":
    default:
      return value;
  }
}

/**
 * Formatea un valor del backend para el frontend
 */
function formatValueForFrontend(
  value: string | number | boolean | null,
  variableType: string,
): string {
  if (value === null || value === undefined) return "";

  switch (variableType) {
    case "boolean":
      return String(value);
    case "number":
      return String(value);
    case "string":
    case "date":
    case "array":
    default:
      return String(value);
  }
}

/**
 * Valida un nodo calculado serializado
 */
export function validateSerializedCalculatedNode(
  node: unknown,
): node is SerializedCalculatedNode {
  if (!node || typeof node !== "object") return false;

  const nodeObj = node as Record<string, unknown>;
  const requiredFields = [
    "ref_name",
    "display_name",
    "position_x",
    "position_y",
    "type",
  ];
  const hasRequiredFields = requiredFields.every((field) => field in nodeObj);

  if (!hasRequiredFields) return false;
  if (nodeObj.type !== "CALCULATED") return false;

  // Validar que tenga inputs calculados
  if (nodeObj.inputs && Array.isArray(nodeObj.inputs)) {
    const hasCalculatedInput = nodeObj.inputs.some((input: unknown) => {
      if (!input || typeof input !== "object") return false;
      const inputObj = input as Record<string, unknown>;
      return (
        inputObj.type === "CALCULATED" &&
        inputObj.calculations &&
        Array.isArray(inputObj.calculations)
      );
    });
    return hasCalculatedInput;
  }

  return true;
}

/**
 * Convierte elementos del componente a formato JSON para debug
 */
export function elementsToJSON(
  elements: CalculationElement[],
  operation: Operation,
  variableType: string,
  selectedVariable?: string,
  selectedDateGetFormat?: string,
  selectedDateToStringFormat?: string,
): string {
  const serialized = serializeCalculationElements(
    elements,
    operation,
    variableType,
    selectedVariable,
    undefined, // No tenemos variables aquí
    selectedDateGetFormat,
    selectedDateToStringFormat,
  );
  return JSON.stringify(serialized, null, 2);
}

/**
 * Convierte JSON a elementos del componente para debug
 */
export function JSONToElements(json: string): {
  elements: CalculationElement[];
  operation: string;
  variableType: string;
  selectedVariable?: string;
} | null {
  try {
    const parsed = JSON.parse(json);

    // Detectar si es un nodo completo o solo un input calculado
    if (parsed.type === "CALCULATED" && parsed.inputs) {
      // Es un nodo calculado completo
      const result = deserializeCalculatedNode(
        parsed as SerializedCalculatedNode,
      );
      return {
        elements: result.elements,
        operation: result.operation,
        variableType: result.variableType,
        selectedVariable: result.selectedVariable,
      };
    } else if (parsed.type === "CALCULATED" && parsed.calculations) {
      // Es solo un input calculado
      const result = deserializeCalculatedInput(
        parsed as SerializedCalculatedInput,
      );
      return {
        elements: result.elements,
        operation: result.operation,
        variableType: result.variableType,
        selectedVariable: result.selectedVariable,
      };
    } else {
      console.error("Formato JSON no reconocido");
      return null;
    }
  } catch (error) {
    console.error("Error parsing JSON:", error);
    return null;
  }
}

/**
 * Deserializa datos del backend con relationships completos
 */
/**
 * Deserializa un input calculado del backend al frontend usando conexiones del API.
 *
 * Esta función maneja tanto variables de formulario como variables del centro de datos:
 * - **Variables de FORMULARIO**: Tienen `field.id` con el ID específico del campo
 * - **Variables de CENTRO DE DATOS**: Tienen `field` como null y usan `source_node_id` del nodo
 *
 * @param calculatedInput - Datos del backend con estructura de conexiones y relationships
 * @param variables - Variables disponibles para buscar la correcta por nodeId y output_name uwu
 * @returns Objeto con elementos, operación, tipo de variable y variable seleccionada para la UI
 *
 * @example
 * // Para variable de formulario:
 * {
 *   source_connections: [{
 *     field: { id: "campo-123", type: "text" }
 *   }]
 * }
 *
 * @example
 * // Para variable de centro de datos:
 * {
 *   source_connections: [{
 *     source_node_id: "nodo-456",
 *     field: null
 *   }]
 * }
 */
export function deserializeCalculatedInputWithConnections(
  calculatedInput: Record<string, unknown>,
  variables?: NodeVariable[],
): {
  elements: CalculationElement[];
  operation: string;
  variableType: string;
  selectedVariable?: string;
  selectedDateGetFormat?: string;
  selectedDateToStringFormat?: string;
} {
  const calculations = calculatedInput.calculations as
    | Array<{
        operation: string;
        inputs: Array<{
          pivot?: { order: number };
          source_connections?: Array<{
            field?: { id: string; type: string };
            source_node_id?: string;
            target_node_id?: string;
            target_node_input_id?: string;
            field_id?: string | null;
            output_name?: string | null;
          }>;
          global_type?: string;
          value?: string | number | boolean;
          type?: string;
        }>;
      }>
    | undefined;

  if (!calculations || calculations.length === 0) {
    return {
      elements: [],
      operation: "",
      variableType: "string",
    };
  }

  const calculation = calculations[0];
  const operation =
    REVERSE_OPERATION_MAPPING[calculation.operation as CalculationOperation] ||
    "concatenate";

  // Ordenar inputs por order
  const sortedInputs = calculation.inputs.sort(
    (a, b) => (a.pivot?.order || 0) - (b.pivot?.order || 0),
  );

  // El primer input determina tipo y variable
  const firstInput = sortedInputs[0];
  let variableType = "string";
  let selectedVariable: string | undefined;

  if (
    firstInput?.source_connections &&
    firstInput.source_connections.length > 0
  ) {
    const sourceConnection = firstInput.source_connections[0];
    if (sourceConnection.field_id && sourceConnection.output_name) {
      // Variable de formulario - tiene field_id y output_name
      variableType = mapFieldTypeToVariableType(
        sourceConnection.field?.type || "string",
      );
      selectedVariable = `${sourceConnection.field_id}-${sourceConnection.output_name}`;
    } else if (sourceConnection.field && sourceConnection.field.id) {
      // Variable de formulario - tiene field con id
      variableType = mapFieldTypeToVariableType(sourceConnection.field.type);
      selectedVariable = sourceConnection.field.id;
    } else if (
      sourceConnection.output_name &&
      sourceConnection.source_node_id
    ) {
      // Variable con output_name (expediente, interoperabilidad, etc.)
      variableType = mapBackendTypeToVariable(
        (firstInput?.type || "STRING") as PrimitiveType,
      );

      // Buscar la variable correcta en la lista de variables disponibles
      if (variables) {
        const nodeVariables = variables.find(
          (nv) => nv.nodeId === sourceConnection.source_node_id,
        );

        if (nodeVariables) {
          const foundVariable = nodeVariables.variables.find(
            (v) => v.output_name === sourceConnection.output_name,
          );

          if (foundVariable) {
            selectedVariable = foundVariable.id;
          } else {
            // Fallback: construir ID con formato antiguo
            selectedVariable = `${sourceConnection.source_node_id}-${sourceConnection.output_name}`;
          }
        } else {
          // Fallback: construir ID con formato antiguo
          selectedVariable = `${sourceConnection.source_node_id}-${sourceConnection.output_name}`;
        }
      } else {
        // Sin variables disponibles, usar formato antiguo
        selectedVariable = `${sourceConnection.source_node_id}-${sourceConnection.output_name}`;
      }
    } else if (sourceConnection.source_node_id) {
      // Variable del centro de datos - usar source_node_id
      selectedVariable = sourceConnection.source_node_id;
      // Para variables del centro de datos, el tipo es generalmente array
      variableType = mapBackendTypeToVariable(
        (firstInput?.type || "ARRAY") as PrimitiveType,
      );
    }
  } else if (
    firstInput?.global_type === "APPLICANT" ||
    firstInput?.global_type === "CURRENT_TIME"
  ) {
    variableType = mapBackendTypeToVariable(
      (firstInput?.type || "STRING") as PrimitiveType,
    );
    selectedVariable = firstInput.value as string;
  } else if (
    firstInput.global_type === "NODE_EXECUTION_FINISH" ||
    firstInput.global_type === "NODE_EXECUTION_START"
  ) {
    variableType = "date";
    if (firstInput.global_type === "NODE_EXECUTION_START") {
      selectedVariable = `${firstInput.value}_execution_start`;
    } else {
      selectedVariable = `${firstInput.value}_execution_finish`;
    }
  } else {
    variableType = mapBackendTypeToVariable(
      (firstInput?.type || "STRING") as PrimitiveType,
    );
  }

  // Los inputs adicionales son elementos o formatos
  const additionalInputs = sortedInputs.filter(
    (input) => (input.pivot?.order || 0) > 0,
  );

  // Extraer formatos de fecha si existen (basado en el nombre o tipo del input)
  let selectedDateGetFormat: string | undefined;
  let selectedDateToStringFormat: string | undefined;

  // Los elementos reales (necesito distinguir formatos de elementos reales)
  // Para la función with connections, los formatos vendrán como values sin conexiones
  const realElements = additionalInputs.filter((input) => {
    // Si tiene source_connections, es un elemento real (variable)
    if (input.source_connections && input.source_connections.length > 0) {
      return true;
    }

    // Si no tiene conexiones, podría ser un formato o un valor estático
    // Los formatos suelen ser strings simples para operaciones de fecha
    if (
      (operation === "date_get_format" || operation === "date_to_string") &&
      typeof input.value === "string" &&
      !input.value.includes("{")
    ) {
      // No es JSON de tiempo

      if (operation === "date_get_format") {
        selectedDateGetFormat = input.value as string;
        return false; // No es un elemento real
      } else if (operation === "date_to_string") {
        selectedDateToStringFormat = input.value as string;
        return false; // No es un elemento real
      }
    }

    return true; // Es un elemento real
  });

  const elements: CalculationElement[] = realElements.map((input, index) => {
    const baseElement = {
      id: `element_${index}`,
      type: "inp" as const,
      value: "",
    } as CalculationElement;
    if (input.source_connections && input.source_connections.length > 0) {
      const sourceConnection = input.source_connections[0];

      if (sourceConnection.field_id && sourceConnection.output_name) {
        // Variable de formulario - tiene field_id y output_name
        variableType = mapFieldTypeToVariableType(
          sourceConnection.field?.type || "string",
        );
        baseElement.value = `${sourceConnection.field_id}-${sourceConnection.output_name}`;
        baseElement.type = "var";
      } else if (sourceConnection.field && sourceConnection.field.id) {
        // Variable de formulario - usar field.id
        baseElement.value = sourceConnection.field.id;
        baseElement.type = "var";
      } else if (
        sourceConnection.output_name &&
        sourceConnection.source_node_id
      ) {
        // Variable con output_name (expediente, interoperabilidad, etc.)
        baseElement.type = "var";

        // Buscar la variable correcta en la lista de variables disponibles
        if (variables) {
          const nodeVariables = variables.find(
            (nv) => nv.nodeId === sourceConnection.source_node_id,
          );
          if (nodeVariables) {
            const foundVariable = nodeVariables.variables.find(
              (v) => v.output_name === sourceConnection.output_name,
            );
            if (foundVariable) {
              baseElement.value = foundVariable.id;
            } else {
              // Fallback: construir ID con formato antiguo
              baseElement.value = `${sourceConnection.source_node_id}-${sourceConnection.output_name}`;
            }
          } else {
            // Fallback: construir ID con formato antiguo
            baseElement.value = `${sourceConnection.source_node_id}-${sourceConnection.output_name}`;
          }
        } else {
          // Sin variables disponibles, usar formato antiguo
          baseElement.value = `${sourceConnection.source_node_id}-${sourceConnection.output_name}`;
        }
      } else if (sourceConnection.source_node_id) {
        // Variable del centro de datos - usar source_node_id
        baseElement.value = sourceConnection.source_node_id;
        baseElement.type = "var";
      }
    } else if (
      input?.global_type === "APPLICANT" ||
      input?.global_type === "CURRENT_TIME"
    ) {
      baseElement.value = input.value as string;
      baseElement.type = "var";
    } else if (
      input.global_type === "NODE_EXECUTION_FINISH" ||
      input.global_type === "NODE_EXECUTION_START"
    ) {
      baseElement.type = "var";
      if (input.global_type === "NODE_EXECUTION_START") {
        baseElement.value = `${input.value}_execution_start`;
      } else {
        baseElement.value = `${input.value}_execution_finish`;
      }
    } else {
      // Es un valor estático
      baseElement.type = "inp";

      // Para operaciones de fecha que usan campos específicos (date_add, date_subtract)
      if (
        (operation === "date_add" || operation === "date_subtract") &&
        variableType === "date"
      ) {
        try {
          // Intentar parsear como JSON para obtener los campos de tiempo
          const timeValue = JSON.parse(input.value as string);
          baseElement.hours = timeValue.hours || "";
          baseElement.days = timeValue.days || "";
          baseElement.months = timeValue.months || "";
          baseElement.years = timeValue.years || "";
          baseElement.value = ""; // No usar el campo value para estas operaciones
        } catch {
          // Si no es JSON válido, tratar como valor normal
          baseElement.value = formatValueForFrontend(
            input.value || null,
            variableType,
          );
        }
      } else {
        console.log(input.value);
        baseElement.value = formatValueForFrontend(
          input.value || null,
          variableType,
        );
      }
    }

    return baseElement;
  });

  return {
    elements,
    operation,
    variableType,
    selectedVariable,
    selectedDateGetFormat,
    selectedDateToStringFormat,
  };
}

/**
 * Mapea tipos de campo del backend a tipos de variable del frontend.
 *
 * Soporta tipos tanto de formularios como de centro de datos:
 * - **Formularios**: text, textarea, email, number, date, boolean, select, etc.
 * - **Centro de datos**: array, string, integer, decimal, etc.
 *
 * @param fieldType - Tipo de campo del backend (case-insensitive)
 * @returns Tipo de variable del frontend compatible con la UI
 */
function mapFieldTypeToVariableType(fieldType: string): VariableType {
  switch (fieldType.toLowerCase()) {
    case "text":
    case "textarea":
    case "email":
    case "identification":
    case "string":
      return "string";
    case "number":
    case "integer":
    case "decimal":
      return "number";
    case "date":
    case "datetime":
    case "timestamp":
      return "date";
    case "boolean":
    case "checkbox":
      return "boolean";
    case "select":
    case "radio":
    case "array":
      return "array";
    default:
      return "string";
  }
}
